import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import { supabase } from "@/lib/supabase";
import { showNotification } from "../../../../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";

interface PayoutAkontoDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
	organizationId: string;
	recipientName: string;
	recipientClientId: string | null;
	recipientGuarantorId: string | null;
	recipientPatientId: string | null;
	outstandingAmount: number;
	currency: string;
}

export const PayoutAkontoDialog: React.FC<PayoutAkontoDialogProps> = ({
	open,
	onOpenChange,
	onSuccess,
	organizationId,
	recipientName,
	recipientClientId,
	recipientGuarantorId,
	recipientPatientId,
	outstandingAmount,
	currency,
}) => {
	const [amount, setAmount] = useState("");
	const [note, setNote] = useState("");
	const [loading, setLoading] = useState(false);

	const handleSubmit = async () => {
		const numAmount = parseFloat(amount);
		if (isNaN(numAmount) || numAmount <= 0) {
			showNotification({
				message: "Bitte einen gültigen Betrag eingeben",
				type: "error",
			});
			return;
		}
		if (numAmount > outstandingAmount) {
			showNotification({
				message: `Betrag übersteigt verfügbares Guthaben (${outstandingAmount.toFixed(
					2
				)} ${currency})`,
				type: "error",
			});
			return;
		}

		setLoading(true);
		const { data, error } = await (supabase.rpc as any)(
			"create_rueckverguetung",
			{
				p_organization_id: organizationId,
				p_amount: numAmount,
				p_recipient_client_id: recipientClientId,
				p_recipient_guarantor_id: recipientGuarantorId,
				p_recipient_patient_id: recipientPatientId,
				p_currency: currency,
				p_note: note || null,
			}
		);

		setLoading(false);

		if (error || !data?.success) {
			Logger.error("Error creating Rückvergütung", error || data?.error);
			showNotification({
				message:
					data?.error || "Fehler beim Erstellen der Rückvergütung",
				type: "error",
			});
			return;
		}

		showNotification({
			message: `Rückvergütung ${data.doc_number} über ${numAmount.toFixed(
				2
			)} ${currency} erstellt`,
			type: "success",
		});
		setAmount("");
		setNote("");
		onOpenChange(false);
		onSuccess();
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			setAmount("");
			setNote("");
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Guthaben auszahlen</DialogTitle>
					<DialogDescription>
						Auftraggeberguthaben für{" "}
						<strong>{recipientName}</strong> als Rückvergütung
						ausbuchen.
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="text-sm text-gray-600">
						Verfügbares Guthaben:{" "}
						<span className="font-mono font-medium">
							{outstandingAmount.toFixed(2)} {currency}
						</span>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="payout-amount">Betrag</Label>
						<Input
							id="payout-amount"
							type="number"
							step="0.05"
							min="0.01"
							max={outstandingAmount}
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							placeholder={outstandingAmount.toFixed(2)}
							aria-label="Auszahlungsbetrag"
							data-testid="payout-amount-input"
						/>
					</div>
					<div className="grid gap-2">
						<Label htmlFor="payout-note">Notiz (optional)</Label>
						<Textarea
							id="payout-note"
							value={note}
							onChange={(e) => setNote(e.target.value)}
							placeholder="Auftraggeberguthaben ausgebucht"
							rows={2}
							aria-label="Notiz zur Rückvergütung"
							data-testid="payout-note-input"
						/>
					</div>
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						disabled={loading}
					>
						Abbrechen
					</Button>
					<Button
						onClick={handleSubmit}
						disabled={loading || !amount}
						data-testid="payout-confirm-button"
					>
						{loading ? "Wird erstellt..." : "Auszahlen"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
